import { CategoryTreeItem } from '@zonofi/zono-money-store';
import React, { useCallback, useMemo, useState } from 'react';
import { ChipFilters, SearchComponent } from '../../components';
import { ChipFilterSection } from '../../types';
import { NestedCategories } from '../nested-categories/nested-categories';
import './product-filters.scss';
import { ReactComponent as BackIcon } from '@zonofi/svg-icons/arrow_left.svg';

export interface ProductFiltersProps {
  segments: ChipFilterSection[];
  categories: CategoryTreeItem;
  updateSelectedSegments: (val: string) => void;
  updateSelectedCategories: (val: CategoryTreeItem[]) => void;
  search?: SearchComponent;
  styles?: React.CSSProperties;
}

type ProductFilterType = '' | 'segments' | 'categories';

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  segments,
  categories,
  updateSelectedCategories,
  updateSelectedSegments,
  search,
  styles = {} as React.CSSProperties,
}) => {
  const [selectedFilterType, setSelectedFilterType] =
    useState<ProductFilterType>('');
  const [showBack, setShowBack] = useState(false);

  const initialProductFilters: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [{ id: '', selected: selectedFilterType === '', value: 'All' }],
        onItemPress: ({ id }) => {
          setSelectedFilterType(id as ProductFilterType);
        },
      },
      {
        items: [
          {
            id: 'segments',
            selected: selectedFilterType === 'segments',
            value: 'Segments',
          },
          {
            id: 'categories',
            selected: selectedFilterType === 'categories',
            value: 'Categories',
          },
        ],
        onItemPress: ({ id }) => {
          setSelectedFilterType(id as ProductFilterType);
          setShowBack(true);
        },
      },
    ],
    [selectedFilterType]
  );

  const segmentSections: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: 'segments',
            selected: selectedFilterType === 'segments',
            value: 'Segments',
          },
        ],
        onItemPress: () => {},
      },
      ...(segments || []),
    ],
    [segments, selectedFilterType]
  );

  const categoriesSection: (ChipFilterSection | JSX.Element)[] = useMemo(
    () => [
      {
        items: [
          {
            id: 'categories',
            selected: selectedFilterType === 'categories',
            value: 'Categories',
          },
        ],
        onItemPress: () => {},
      },
      <NestedCategories
        categories={categories}
        updateSelectedCategories={updateSelectedCategories}
      />,
    ],
    [categories, selectedFilterType, updateSelectedCategories]
  );

  const filterData: (ChipFilterSection | JSX.Element)[] = useMemo(() => {
    return selectedFilterType === 'segments'
      ? segmentSections
      : selectedFilterType === 'categories'
        ? categoriesSection
        : initialProductFilters;
  }, [
    categoriesSection,
    initialProductFilters,
    segmentSections,
    selectedFilterType,
  ]);

  const renderBackBtn = useCallback(() => {
    return showBack ? (
      <div
        onClick={() => {
          if (selectedFilterType === 'segments') {
            updateSelectedSegments('');
          } else if (selectedFilterType === 'categories') {
            updateSelectedCategories([]);
          }
          setSelectedFilterType('');
          setShowBack(false);
        }}
      >
        <BackIcon color="#262626" />
      </div>
    ) : null;
  }, [showBack]);

  if (segments.length === 0 && categories === undefined) return null;

  return (
    <ChipFilters
      sections={filterData}
      startElement={renderBackBtn()}
      chipFilterStyles={{
        alignSelf: 'flex-start',
        width: '100%',
        position: 'sticky',
        top: 0,
        left: 0,
        ...styles,
      }}
      search={search}
    />
  );
};
